/**
 * @file HowToBecomeABcaba.jsx
 * @description Standalone educational/career-intent SEO page at
 *   /how-to-become-a-bcaba — explains, at a category level only, the general
 *   BACB pathway to becoming a Board Certified Assistant Behavior Analyst
 *   (BCaBA), then bridges into how ABAcases helps once someone is certified
 *   (or in process) and ready to find real case work. Deliberately
 *   conservative: no specific numeric requirements (fieldwork hours, CEU
 *   counts, exam question/time counts, fees, ages), no certification /
 *   training / supervision / licensure claims by ABAcases, no state-specific
 *   legal claims, no active case or provider counts. Links out, for real,
 *   three times to authoritative official BACB pages (BCaBA overview,
 *   upcoming requirement changes, examination information). Mirrors the
 *   structure of HowToBecomeAnRbt.jsx (hero, disclaimer banner, prose + card
 *   sections, FAQ with JSON-LD, gradient closing CTA, PublicFooter).
 *
 *   Rendered under TopNav (guest nav), so it has no brand header of its own.
 * @module components/HowToBecomeABcaba
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  UsersIcon, ShieldIcon, LayersIcon, AwardIcon, BellIcon,
  InfoIcon, ExternalLinkIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import GuideReviewStamp from "./GuideReviewStamp";
import { GuideAtAGlance, GuideYourPath } from "./GuideShared";
import { setSignupIntent } from "../lib/signupIntent";
import { useFaqJsonLd, useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A, ctaMeta } from "../lib/analytics";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

const BACB_BCABA_URL = "https://www.bacb.com/bcaba/";
const BACB_CHANGES_URL = "https://www.bacb.com/upcoming-changes/";
const BACB_EXAM_URL = "https://www.bacb.com/examination-information/";

// General-pathway steps, described in categories only — no specific hour
// counts, CEU counts, fees, or ages. Exact current requirements live at
// bacb.com, not here.
// (title/body rendered via t() using titleKey/bodyKey — see howToBecomeBcaba
// namespace; English source strings live in the translation catalog.)
const STEPS = [
  { titleKey: "howToBecomeBcaba.step1Title", bodyKey: "howToBecomeBcaba.step1Body" },
  { titleKey: "howToBecomeBcaba.step2Title", bodyKey: "howToBecomeBcaba.step2Body" },
  { titleKey: "howToBecomeBcaba.step3Title", bodyKey: "howToBecomeBcaba.step3Body" },
  { titleKey: "howToBecomeBcaba.step4Title", bodyKey: "howToBecomeBcaba.step4Body" },
  { titleKey: "howToBecomeBcaba.step5Title", bodyKey: "howToBecomeBcaba.step5Body" },
  { titleKey: "howToBecomeBcaba.step6Title", bodyKey: "howToBecomeBcaba.step6Body" },
];

const ROLE_ITEMS = [
  { Icon: ShieldIcon, titleKey: "howToBecomeBcaba.role1Title", bodyKey: "howToBecomeBcaba.role1Body" },
  { Icon: UsersIcon, titleKey: "howToBecomeBcaba.role2Title", bodyKey: "howToBecomeBcaba.role2Body" },
  { Icon: LayersIcon, titleKey: "howToBecomeBcaba.role3Title", bodyKey: "howToBecomeBcaba.role3Body" },
  { Icon: AwardIcon, titleKey: "howToBecomeBcaba.role4Title", bodyKey: "howToBecomeBcaba.role4Body" },
];

// SINGLE SOURCE OF TRUTH: the visible FAQ cards and the FAQPage JSON-LD both
// derive from these i18n keys (via faqJsonLd in-component). externalLink marks
// the items that render an inline BACB link; the JSON-LD reconstructs that link
// as plain text so schema and visible text stay identical.
const FAQ_KEYS = [
  { qKey: "howToBecomeBcaba.faq1Q", aKey: "howToBecomeBcaba.faq1A" },
  { qKey: "howToBecomeBcaba.faq2Q", aBeforeKey: "howToBecomeBcaba.faq2ABefore", aAfterKey: "howToBecomeBcaba.faq2AAfter", externalLink: "bcaba" },
  { qKey: "howToBecomeBcaba.faq3Q", aBeforeKey: "howToBecomeBcaba.faq3ABefore", aAfterKey: "howToBecomeBcaba.faq3AAfter", externalLink: "changes" },
  { qKey: "howToBecomeBcaba.faq4Q", aKey: "howToBecomeBcaba.faq4A" },
];

export default function HowToBecomeABcaba({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  usePageSeo({
    title: "How to Become a BCaBA | ABA Career Guide",
    description: "Learn what a BCaBA is, the general path toward BCaBA certification, what to confirm with the BACB, and how ABAcases helps providers find ABA case opportunities.",
    canonical: "/how-to-become-a-bcaba",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-how-to-become-a-bcaba", [
    { name: "Home", url: "/" },
    { name: "How to Become a BCaBA", url: "/how-to-become-a-bcaba" },
  ]);
  const faqJsonLd = FAQ_KEYS.map((k) => {
    let a;
    if (k.externalLink === "bcaba") a = `${t(k.aBeforeKey)} bacb.com/bcaba ${t(k.aAfterKey)}`;
    else if (k.externalLink === "changes") a = `${t(k.aBeforeKey)} bacb.com/upcoming-changes ${t(k.aAfterKey)}`;
    else a = t(k.aKey);
    return { q: t(k.qKey), a };
  });
  useFaqJsonLd("faq-jsonld-how-to-become-a-bcaba", faqJsonLd);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as AboutAbaCases / HowToBecomeAnRbt.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  // Real, external, off-domain links to authoritative BACB sources — the only
  // places on this page that intentionally leave abacases.com.
  const BacbLink = ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: INDIGO, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
      {children} <ExternalLinkIcon size={12} color={INDIGO} />
    </a>
  );

  const goJoin = () => {
    track(A.RESOURCE_PAGE_CTA_CLICK, {
      supabaseMeta: { page: "/how-to-become-a-bcaba/" },
      ga4Params: ctaMeta({
        pagePath: "/how-to-become-a-bcaba/",
        pageType: "resource",
        ctaLabel: "Join ABA case alerts",
        targetPath: "/auth",
        userLanguage: i18n.language,
        audience: "resource",
      }),
    });
    setSignupIntent("alerts");
    onNavigate("auth");
  };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs ariaLabel={t('howToBecomeBcaba.breadcrumbAriaLabel')} items={[
            { label: t('howToBecomeBcaba.breadcrumbHome'), onClick: () => onNavigate("home") },
            { label: t('howToBecomeBcaba.breadcrumbCurrent') },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t('howToBecomeBcaba.badgeCareerGuide')}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>abacases.com</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t('howToBecomeBcaba.heroTitle')}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 16px" }}>
            {t('howToBecomeBcaba.heroSubtitle')}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 640, margin: "0 0 28px" }}>
            {t('howToBecomeBcaba.heroBody')}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={goJoin} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: `linear-gradient(135deg, ${INDIGO} 0%, #6366F1 100%)`, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
              <BellIcon size={16} color="#fff" /> {t('howToBecomeBcaba.ctaJoinAlerts')}
            </button>
          </div>
        </div>
      </header>

      {/* ── Disclaimer banner ────────────────────────────────── */}
      <div style={{ background: "#eef2ff", borderBottom: `1px solid ${BORDER}`, padding: isMobile ? "16px 18px" : "18px 48px" }}>
        <div style={{ maxWidth: maxW, margin: "0 auto", display: "flex", alignItems: "flex-start", gap: 12 }}>
          <InfoIcon size={17} color={INDIGO} style={{ flexShrink: 0, marginTop: 2 }} />
          <span style={{ fontSize: 13.5, color: NAVY, fontWeight: 600, lineHeight: 1.6 }}>
            {t('howToBecomeBcaba.disclaimerBannerText')} <BacbLink href={BACB_BCABA_URL}>bacb.com/bcaba</BacbLink>.
          </span>
        </div>
      </div>

      {/* ── At a glance ──────────────────────────────────────── */}
      <GuideAtAGlance
        isMobile={isMobile}
        title={t('guideShared.atGlanceTitle')}
        items={[1, 2, 3, 4, 5, 6].map((n) => t(`howToBecomeBcaba.atGlanceItem${n}`))}
        confirmPre={t('howToBecomeBcaba.atGlanceConfirm')}
        confirmHref={BACB_BCABA_URL}
        confirmLabel="bacb.com/bcaba"
      />

      {/* ── 1. What is a BCaBA? ───────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcaba.whatIsTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('howToBecomeBcaba.whatIsPara1')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeBcaba.whatIsPara2')}
          </p>
        </div>
      </section>

      {/* ── 2. What BCaBAs do in ABA services ────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcaba.rolesTitle')}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16 }}>
            {ROLE_ITEMS.map((c) => (
              <div key={c.titleKey} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={19} color={INDIGO} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 5 }}>{t(c.titleKey)}</div>
                  <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }}>{t(c.bodyKey)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. BCaBA vs BCBA at a high level ─────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcaba.comparisonTitle')}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16, marginBottom: 20 }}>
            <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 8 }}>{t('howToBecomeBcaba.comparisonBcabaLabel')}</div>
              <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.65 }}>
                {t('howToBecomeBcaba.comparisonBcabaBody')}
              </div>
            </div>
            <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 8 }}>{t('howToBecomeBcaba.comparisonBcbaLabel')}</div>
              <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.65 }}>
                {t('howToBecomeBcaba.comparisonBcbaBody')}
              </div>
            </div>
          </div>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeBcaba.comparisonLinksBefore')}{" "}
            <SeoLink to="rbt-vs-bt-vs-bcba-vs-bcaba" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcaba.comparisonRbtVsLabel')}</SeoLink>{" "}
            {t('howToBecomeBcaba.comparisonLinksMiddle')}{" "}
            <SeoLink to="how-to-become-a-bcba" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcaba.comparisonBcbaGuideLabel')}</SeoLink>
            {t('howToBecomeBcaba.comparisonLinksAfter')}
          </p>
        </div>
      </section>

      {/* ── 4. General steps to become a BCaBA ───────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 16px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcaba.stepsTitle')}
          </h2>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, marginBottom: 22 }}>
            {t('howToBecomeBcaba.stepsIntro')}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 22 }}>
            {STEPS.map((s, i) => (
              <div key={s.titleKey} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "16px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: INDIGO, color: "#fff", fontWeight: 800, fontSize: 13.5, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 4 }}>{t(s.titleKey)}</div>
                  <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.6 }}>{t(s.bodyKey)}</div>
                </div>
              </div>
            ))}
          </div>
          <div data-testid="bcaba-changes-note" style={{ background: "#f5f3ff", border: `1px solid ${BORDER}`, borderLeft: `4px solid ${INDIGO}`, borderRadius: 12, padding: isMobile ? "16px 18px" : "18px 24px", marginBottom: 18 }}>
            <p style={{ fontSize: 15, color: NAVY, fontWeight: 700, lineHeight: 1.65, margin: "0 0 8px" }}>
              {t('howToBecomeBcaba.changesNotePre')} <BacbLink href={BACB_BCABA_URL}>bacb.com/bcaba</BacbLink>.
            </p>
            <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.65, margin: 0 }}>
              {t('howToBecomeBcaba.rbtFirstPre')}{" "}
              <SeoLink to="how-to-become-an-rbt" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcaba.rbtFirstLink')}</SeoLink>.
            </p>
          </div>
          <p style={{ fontSize: 15, color: NAVY, fontWeight: 600, lineHeight: 1.7 }}>
            {t('howToBecomeBcaba.stepsFooterText')} <BacbLink href={BACB_BCABA_URL}>bacb.com/bcaba</BacbLink>.
          </p>
        </div>
      </section>

      {/* ── 5. What to confirm with the BACB ─────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcaba.confirmTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeBcaba.confirmBacbBefore')} <BacbLink href={BACB_CHANGES_URL}>bacb.com/upcoming-changes</BacbLink> {t('howToBecomeBcaba.confirmBacbAfter')}
          </p>
        </div>
      </section>

      {/* ── 6. BCaBA exam and application basics ─────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcaba.examTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('howToBecomeBcaba.examPara1')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeBcaba.examFooterText')} <BacbLink href={BACB_EXAM_URL}>bacb.com/examination-information</BacbLink>.
          </p>
        </div>
      </section>

      {/* ── 7. How BCaBAs fit into the ABA team ──────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcaba.fitTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('howToBecomeBcaba.fitPara1')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeBcaba.fitLinkBefore')}{" "}
            <SeoLink to="aba-roles-guide" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcaba.fitAbaRolesGuideLabel')}</SeoLink>.
          </p>
        </div>
      </section>

      {/* ── 8. How ABAcases helps BCaBAs find better-fit opportunities ── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcaba.helpTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('howToBecomeBcaba.helpPara1Before')}{" "}
            <SeoLink to="aba-provider-waitlist" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcaba.helpCaseAlertsLabel')}</SeoLink>{" "}
            {t('howToBecomeBcaba.helpPara1Middle')}{" "}
            <SeoLink to="aba-case-finder" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcaba.helpCaseFinderLabel')}</SeoLink>{" "}
            {t('howToBecomeBcaba.helpPara1After')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeBcaba.helpPara2Before')}{" "}
            <SeoLink to="about-aba-cases" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcaba.helpAboutAbaCasesLabel')}</SeoLink>
            {t('howToBecomeBcaba.helpPara2After')}
          </p>
        </div>
      </section>

      {/* ── 9. FAQ ────────────────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 24px", letterSpacing: "-0.02em", textAlign: "center" }}>
            {t('howToBecomeBcaba.faqTitle')}
          </h2>
          {FAQ_KEYS.map((k, i) => {
            return (
              <div key={i} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "18px 20px", marginBottom: 10 }}>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: NAVY, marginBottom: 8 }}>{t(k.qKey)}</div>
                <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.7 }}>
                  {k.externalLink === "bcaba" ? (
                    <>
                      {t(k.aBeforeKey)} <BacbLink href={BACB_BCABA_URL}>bacb.com/bcaba</BacbLink> {t(k.aAfterKey)}
                    </>
                  ) : k.externalLink === "changes" ? (
                    <>
                      {t(k.aBeforeKey)} <BacbLink href={BACB_CHANGES_URL}>bacb.com/upcoming-changes</BacbLink> {t(k.aAfterKey)}
                    </>
                  ) : t(k.aKey)}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Source/verification note ──────────────────────────── */}
      <div style={{ padding: isMobile ? "20px 18px" : "22px 48px", textAlign: "center", background: "#fff" }}>
        <p style={{ fontSize: 12, color: TEXT_MID, lineHeight: 1.6, maxWidth: 640, margin: "0 auto" }}>
          {t('howToBecomeBcaba.sourceNote')}
        </p>
      </div>

      <GuideYourPath current="how-to-become-a-bcaba" onNavigate={onNavigate} isMobile={isMobile} />
      <GuideReviewStamp slug="how-to-become-a-bcaba" />
      <PublicFooter />
    </div>
  );
}
